import React, { useCallback } from 'react'
import styled from 'styled-components'
import { ArrowLeft } from 'lucide-react'
import ReactFlow, {
  Background,
  Controls,
  Position,
  MarkerType,
  Node,
  Edge,
  Handle,
} from 'reactflow'
import 'reactflow/dist/base.css'
import 'reactflow/dist/style.css'
import ModelTreeNode, { CustomNode } from './ModelTreeNode'

const GraphContainer = styled.div`
  flex: 1;
  width: 100%;
  background: #1a1a1a;
  position: relative;
`

const BackButton = styled.button`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
  background: rgba(34, 34, 34, 0.9);
  border: none;
  color: #9147ff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  border-radius: 6px;
  backdrop-filter: blur(4px);

  &:hover {
    color: #b366ff;
    background: rgba(34, 34, 34, 0.95);
  }
`

// Custom node with centered handles
const CenteredHandleNode = ({ data, isSelectDisabled }: any) => (
  <CustomNode
    isParent={data.isParent}
    isHighlighted={data.isHighlighted}
    selectable={!isSelectDisabled}
    style={{ position: 'relative' }}
  >
    {data.label}
    <Handle
      type="target"
      position={Position.Top}
      style={{
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#9147ff',
        border: '2px solid #fff',
        width: 16,
        height: 16,
        zIndex: 2,
        pointerEvents: 'none',
      }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      style={{
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#9147ff',
        border: '2px solid #fff',
        width: 16,
        height: 16,
        zIndex: 2,
        pointerEvents: 'none',
      }}
    />
  </CustomNode>
)

interface ModelTreeVisualizationProps {
  parent: any
  onBack?: () => void
  onModelSelect?: (model: any) => void
  project?: any
  highlightModelId?: string
  showBackButton?: boolean
}

const ModelTreeVisualization: React.FC<ModelTreeVisualizationProps> = ({
  parent,
  onBack,
  onModelSelect,
  project,
  highlightModelId,
  showBackButton = true,
}) => {
  // Function to create nodes for the tree visualization
  const createNodes = useCallback(() => {
    // Calculate center position for parent node
    const parentX = 400
    const parentY = -100
    const nodeWidth = 280 // Width of CustomNode
    const nodeHeight = 180 // Approximate height of CustomNode including metrics
    const rowHeight = 400 // Increased vertical spacing between generations
    const nodeSpacing = 450 // Horizontal spacing between sibling nodes
    const minNodeDistance = nodeWidth + 50 // Minimum distance between nodes to prevent overlap

    const nodes: Node[] = []
    const edges: Edge[] = []

    // Track nodes at each depth level to manage horizontal spacing
    const depthMap: { [depth: number]: { id: string; x: number }[] } = {}

    // Helper function to create a node
    const createNodeData = (
      model: any,
      isParent: boolean,
      hasChildren: boolean,
      parentModel?: any
    ) => {
      const isHighlighted = highlightModelId === model.id

      return {
        label: (
          <ModelTreeNode
            model={model}
            isParent={isParent}
            hasChildren={hasChildren}
            parentModel={parentModel}
            project={project}
            isHighlighted={isHighlighted}
          />
        ),
        isParent,
        isHighlighted,
        model,
      }
    }

    // Function to check if a proposed position would overlap with existing nodes
    const wouldOverlap = (x: number, depth: number): boolean => {
      if (!depthMap[depth]) return false

      return depthMap[depth].some((node) => Math.abs(node.x - x) < minNodeDistance)
    }

    // Function to find a non-overlapping position
    const findNonOverlappingPosition = (baseX: number, depth: number): number => {
      if (!wouldOverlap(baseX, depth)) return baseX

      // Try positions to the right and left alternately with increasing distance
      let offset = minNodeDistance
      let direction = 1

      while (true) {
        const testX = baseX + offset * direction
        if (!wouldOverlap(testX, depth)) return testX

        direction *= -1 // Switch direction
        if (direction === 1) offset += minNodeDistance // Increase offset after trying both sides
      }
    }

    // Add parent node
    const rootDepth = 0
    if (!depthMap[rootDepth]) depthMap[rootDepth] = []

    // Add the parent node position to depth map
    depthMap[rootDepth].push({ id: parent.model.id, x: parentX })

    nodes.push({
      id: parent.model.id,
      position: { x: parentX - nodeWidth / 2, y: parentY },
      data: createNodeData(parent.model, true, parent.children && parent.children.length > 0),
      type: 'custom',
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      style: { background: 'transparent', border: 'none' },
    })

    // Recursive function to add child nodes and edges
    const addChildrenNodes = (parentNode: any, children: any[], depth = 0) => {
      // Current depth level for these children
      const currentDepth = depth + 1

      // Initialize the depth map entry if it doesn't exist
      if (!depthMap[currentDepth]) depthMap[currentDepth] = []

      // Find the parent node in the existing nodes array to get its position
      const parentNodeObj = nodes.find((node) => node.id === parentNode.model.id)
      const parentPosition = parentNodeObj ? parentNodeObj.position : { x: parentX, y: parentY }

      // Calculate consistent y position based on depth
      const yPosition = parentPosition.y + rowHeight

      // If there's only one child, try to center it under the parent
      if (children.length === 1) {
        const proposedX = parentPosition.x
        const finalX = findNonOverlappingPosition(proposedX, currentDepth)

        // Add to depth map
        depthMap[currentDepth].push({ id: children[0].model.id, x: finalX })

        // Add child node
        nodes.push({
          id: children[0].model.id,
          position: { x: finalX, y: yPosition },
          data: createNodeData(
            children[0].model,
            false,
            children[0].children && children[0].children.length > 0,
            parentNode.model
          ),
          type: 'custom',
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
          style: { background: 'transparent', border: 'none' },
        })

        // Add edge
        edges.push({
          id: `${parentNode.model.id}-${children[0].model.id}`,
          source: parentNode.model.id,
          target: children[0].model.id,
          type: 'smoothstep',
          animated: true,
          style: {
            stroke: '#9147ff',
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#9147ff',
            width: 20,
            height: 20,
          },
        })

        // Recursively add children
        if (children[0].children && children[0].children.length > 0) {
          addChildrenNodes(children[0], children[0].children, currentDepth)
        }

        return
      }

      // For multiple children, distribute them under the parent
      const totalChildrenWidth = Math.max(children.length * nodeSpacing, nodeSpacing)
      const startXForRow = parentPosition.x - totalChildrenWidth / 2 + nodeSpacing / 2

      // Process each child
      children.forEach((child: any, index: number) => {
        // Calculate initial x position
        const proposedX = startXForRow + index * nodeSpacing

        // Find a non-overlapping position
        const finalX = findNonOverlappingPosition(proposedX, currentDepth)

        // Add to depth map
        depthMap[currentDepth].push({ id: child.model.id, x: finalX })

        // Add child node
        nodes.push({
          id: child.model.id,
          position: { x: finalX, y: yPosition },
          data: createNodeData(
            child.model,
            false,
            child.children && child.children.length > 0,
            parentNode.model
          ),
          type: 'custom',
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
          style: { background: 'transparent', border: 'none' },
        })

        // Add edge
        edges.push({
          id: `${parentNode.model.id}-${child.model.id}`,
          source: parentNode.model.id,
          target: child.model.id,
          type: 'smoothstep',
          animated: true,
          style: {
            stroke: '#9147ff',
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#9147ff',
            width: 20,
            height: 20,
          },
        })

        // Recursively add children of this child
        if (child.children && child.children.length > 0) {
          addChildrenNodes(child, child.children, currentDepth)
        }
      })
    }

    // Start adding children recursively
    if (parent.children && parent.children.length > 0) {
      addChildrenNodes(parent, parent.children)
    }

    return { nodes, edges }
  }, [parent, project, highlightModelId])

  const { nodes, edges } = createNodes()

  return (
    <GraphContainer>
      {showBackButton && (
        <BackButton onClick={onBack}>
          <ArrowLeft size={16} />
          Back to all models
        </BackButton>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        attributionPosition="bottom-right"
        minZoom={0.5}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={onModelSelect !== undefined}
        panOnDrag={true}
        zoomOnScroll={true}
        nodeTypes={{
          custom: (props) => <CenteredHandleNode {...props} isSelectDisabled={!!onModelSelect} />,
        }}
        onNodeClick={(event, node) => {
          if (onModelSelect && node.data && node.data.model) {
            onModelSelect(node.data.model)
          }
        }}
      >
        <Background color="#444" gap={16} />
        <Controls style={{ background: '#2a2a2a' }} />
      </ReactFlow>
    </GraphContainer>
  )
}

export default ModelTreeVisualization
